import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent, DialogClosingEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

/**
 * The modal is shared by every product card, so operation ownership must also be
 * shared. Per-card generations alone cannot stop a slow card A from overwriting
 * the modal after card B has started opening it.
 * @type {{owner: QuickAddComponent, generation: number, controller: AbortController} | null}
 */
let activeQuickAddOperation = null;
let quickAddOperationGeneration = 0;

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {number} */
  #operationGeneration = 0;
  /** @type {AbortController | null} */
  #transientAbortController = null;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.#cartUpdateAbortController.signal.aborted) {
      this.#cartUpdateAbortController = new AbortController();
    }

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#invalidateOpenOperation();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /** Invalidate every pending open/fetch/listener belonging to the current modal operation. */
  #invalidateOpenOperation = () => {
    this.#operationGeneration++;
    if (activeQuickAddOperation?.owner === this) {
      quickAddOperationGeneration++;
      activeQuickAddOperation.controller.abort();
      activeQuickAddOperation = null;
    }
    this.#abortController?.abort();
    this.#abortController = null;
    this.#transientAbortController?.abort();
    this.#transientAbortController = null;
    this.toggleAttribute('stay-visible', false);
  };

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Resolves the product identity owned by this Quick Add trigger. Product
   * cards must agree with the component ID; non-card contexts such as product
   * hotspots keep using the component's required non-empty ID.
   * @returns {string}
   */
  #getExpectedProductId() {
    const componentProductId = this.dataset.productId?.trim() || '';
    if (!componentProductId) return '';

    const productCard = this.closest('product-card');
    if (productCard) {
      const cardProductId = productCard.dataset.productId?.trim() || '';
      if (!cardProductId || cardProductId !== componentProductId) return '';
    }

    return componentProductId;
  }

  /**
   * Validates the exact product form contract inside fetched or live content.
   * @param {Element} container
   * @returns {boolean}
   */
  #hasUsableProductForm(container) {
    const expectedProductId = this.#getExpectedProductId();
    if (!expectedProductId) return false;

    const productForm = container.querySelector('product-form-component[data-product-id]');
    if (!(productForm instanceof HTMLElement)) return false;
    if (productForm.dataset.productId?.trim() !== expectedProductId) return false;

    const form = productForm.querySelector('form[data-type="add-to-cart-form"]');
    if (!(form instanceof HTMLFormElement)) return false;
    const variantInput = form.querySelector('input[name="id"]');
    return variantInput instanceof HTMLInputElement && variantInput.value.trim() !== '';
  }

  /** @returns {boolean} */
  #isUsableModalContent() {
    const modalContent = document.getElementById('quick-add-modal-content');
    return modalContent instanceof HTMLElement && this.#hasUsableProductForm(modalContent);
  }

  /** Close a failed current modal operation and remove any older product UI. */
  #closeAndClearQuickAddModal() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (dialogComponent instanceof QuickAddDialog) void dialogComponent.closeDialog();

    const modalContent = document.getElementById('quick-add-modal-content');
    if (!(modalContent instanceof HTMLElement)) return;
    modalContent.replaceChildren();
    modalContent.removeAttribute('view-event-payload');
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const previousOwner = activeQuickAddOperation?.owner;
    if (previousOwner) previousOwner.#invalidateOpenOperation();
    const sharedController = new AbortController();
    const sharedGeneration = ++quickAddOperationGeneration;
    activeQuickAddOperation = { owner: this, generation: sharedGeneration, controller: sharedController };

    const generation = ++this.#operationGeneration;
    const isCurrent = () =>
      this.isConnected &&
      !sharedController.signal.aborted &&
      generation === this.#operationGeneration &&
      activeQuickAddOperation?.owner === this &&
      activeQuickAddOperation.generation === sharedGeneration &&
      activeQuickAddOperation.controller === sharedController;
    const rejectCurrentOperation = () => {
      // A stale request must never clear a newer owner's live modal.
      if (!isCurrent()) return;
      this.#invalidateOpenOperation();
      this.#closeAndClearQuickAddModal();
    };
    this.#transientAbortController?.abort();
    this.#transientAbortController = sharedController;
    this.toggleAttribute('stay-visible', false);
    const transientSignal = this.#transientAbortController.signal;

    // The modal may already be open for card A while card B is fetching. Bind
    // the close signal before the fetch starts so closing the shared dialog
    // always cancels the current owner and a late response cannot reopen it.
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) {
      rejectCurrentOperation();
      return;
    }
    dialogComponent.addEventListener(DialogClosingEvent.eventName, this.#invalidateOpenOperation, {
      once: true,
      signal: transientSignal,
    });

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      // This path leaves Quick Add entirely. Release shared modal ownership so a
      // missing/blocked navigation can never leave this card as the active owner.
      this.#invalidateOpenOperation();
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);
    if (productGrid && !this.#isUsableProductGrid(productGrid)) {
      this.#cachedContent.delete(currentUrl);
      productGrid = undefined;
    }

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl, transientSignal);
      if (!isCurrent()) return;
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement && this.#isUsableProductGrid(gridElement)) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    // Never open the shared dialog with stale content from a previous product
    // when this product's URL, response, or expected grid is missing.
    if (!productGrid || !this.#isUsableProductGrid(productGrid)) {
      rejectCurrentOperation();
      return;
    }

    // Use a fresh clone from the cache. Updating is synchronous; its boolean
    // result guarantees that the shared modal target really existed and was
    // replaced before the dialog is allowed to open.
    const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
    if (!this.updateQuickAddModal(freshContent)) {
      rejectCurrentOperation();
      return;
    }
    if (!isCurrent()) return;
    this.#updateVariantPicker(productGrid);

    if (!isCurrent()) return;
    // Variant synchronization can mutate the live form. Revalidate the exact
    // product/form/variant contract after every update and before opening.
    if (!this.#isUsableModalContent()) {
      rejectCurrentOperation();
      return;
    }
    if (!this.#openQuickAddModal(transientSignal)) rejectCurrentOperation();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent, signal) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
      signal,
    });
  }

  #openQuickAddModal = (signal) => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return false;
    // Ultimate synchronous guard: showDialog is never reached with empty,
    // stale, incomplete, or mismatched product content.
    if (!this.#isUsableModalContent()) return false;

    this.#stayVisibleUntilDialogCloses(dialogComponent, signal);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return false;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), {
      once: true,
      signal,
    });
    return true;
  };

  #closeQuickAddModal = () => {
    this.#invalidateOpenOperation();
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @param {AbortSignal} operationSignal - Signal owned by the shared modal operation.
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl, operationSignal) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    const requestController = new AbortController();
    this.#abortController = requestController;
    const abortRequest = () => requestController.abort();
    if (operationSignal.aborted) requestController.abort();
    else operationSignal.addEventListener('abort', abortRequest, { once: true });

    try {
      const response = await fetch(productPageUrl, {
        signal: requestController.signal,
      });
      if (requestController.signal.aborted) return null;

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      if (requestController.signal.aborted) return null;
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        console.warn('[quick-add] Product fetch failed:', error);
        return null;
      }
    } finally {
      operationSignal.removeEventListener('abort', abortRequest);
      if (this.#abortController === requestController) this.#abortController = null;
    }
  }

  /**
   * A fetched product grid is safe to cache/open only when it contains the
   * complete add-to-cart form for the product card that started this request.
   * This rejects empty grids, error/login fragments, and stale content from a
   * different product before anything is morphed into the shared dialog.
   * @param {Element} productGrid
   * @returns {boolean}
   */
  #isUsableProductGrid(productGrid) {
    if (!productGrid.matches('[data-product-grid-content]')) return false;
    return this.#hasUsableProductForm(productGrid);
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   * @returns {boolean} Whether the shared modal content was replaced.
   */
  updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent || !this.#isUsableProductGrid(productGrid)) return false;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // Moving the mobile nodes must not weaken the same product/form contract.
    // Re-check immediately before morphing anything into the shared live modal.
    if (!this.#isUsableProductGrid(productGrid)) return false;

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    // Do not continue if morphing failed to leave the exact expected live form.
    if (!this.#isUsableModalContent()) return false;

    this.#syncVariantSelection(modalContent);
    // Syncing may dispatch a change event, so validate again before returning.
    return this.#isUsableModalContent();
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    const signal = this.#abortController?.signal;
    event.promise
      ?.then(({ detail }) => {
        if (signal?.aborted || !this.isConnected) return;
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    const signal = this.#abortController?.signal;
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (signal?.aborted || !this.isConnected) return;
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
